import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useAuth } from '../context/AuthContext';
import { getNote, deleteNote } from '../api/sharedNotes';
import SharedNoteDialog from '../components/shared-notes/SharedNoteDialog';
import SharedNoteVersions from '../components/shared-notes/SharedNoteVersions';
import { createMarkdownComponents } from '../components/common/MarkdownComponents';

const SharedNoteDetail = () => {
  const { noteId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);

  const isAdmin = user && (user.is_staff || user.is_superuser);
  const isDarkMode = document.documentElement.classList.contains('dark');

  const fetchNote = async () => {
    try {
      const data = await getNote(noteId);
      setNote(data);
    } catch (err) {
      console.error('Error fetching note:', err);
      setError('Failed to load the note');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNote();
  }, [noteId]);

  const handleEdit = () => {
    setEditDialogOpen(true);
  };

  const handleDelete = async () => {
    setDeleteDialogOpen(false);
    try {
      await deleteNote(noteId);
      navigate('/shared-notes');
    } catch (err) {
      console.error('Error deleting note:', err);
      setError('Failed to delete the note');
    }
  };

  const handleNoteUpdated = () => {
    setEditDialogOpen(false);
    fetchNote();
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex justify-center items-center min-h-[60vh]">
          <svg className="w-10 h-10 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </div>
      </div>
    );
  }

  if (error || !note) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <button 
          onClick={() => navigate('/shared-notes')}
          className="flex items-center mb-4 text-primary-600 dark:text-primary-400 hover:underline"
        >
          <svg className="w-5 h-5 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Shared Notes
        </button>
        <div className="p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-200 rounded-md border border-red-200 dark:border-red-800">
          {error || 'Note not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <button 
        onClick={() => navigate('/shared-notes')}
        className="flex items-center mb-4 text-primary-600 dark:text-primary-400 hover:underline"
      >
        <svg className="w-5 h-5 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Shared Notes
      </button>
      
      <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md p-6 border border-ui-light-border dark:border-ui-dark-border mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h1 className="text-2xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">
                {note.title}
              </h1>
              {note.is_pinned && (
                <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 13V7a4 4 0 018 0v6a4 4 0 01-8 0z" />
                  <path fillRule="evenodd" d="M6 3a2 2 0 00-2 2v1h10V5a2 2 0 00-2-2H6z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary mb-4">
              <span>Created by {note.created_by.full_name}</span>
              <span>•</span>
              <span>{format(new Date(note.created_at), 'MMM d, yyyy h:mm a')}</span>
              {note.last_edited_by && note.last_edited_by.id !== note.created_by.id && (
                <>
                  <span>•</span>
                  <span>Last edited by {note.last_edited_by.full_name} on {format(new Date(note.updated_at), 'MMM d, yyyy h:mm a')}</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2 mb-4">
              {note.can_edit && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
                  Can Edit
                </span>
              )}
              {note.roles.map(role => (
                <span 
                  key={role.id} 
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-ui-light-background dark:bg-ui-dark-background text-ui-light-text-secondary dark:text-ui-dark-text-secondary border border-ui-light-border dark:border-ui-dark-border"
                >
                  {role.name}
                </span>
              ))}
            </div>
            {note.editors && note.editors.length > 0 && (
              <div className="mb-4">
                <span className="text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                  Editors: {note.editors.map(editor => editor.full_name).join(', ')}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2 mt-4 sm:mt-0">
            <button 
              onClick={() => setVersionsOpen(true)}
              className="p-2 text-ui-light-text-secondary dark:text-ui-dark-text-secondary hover:text-primary-600 dark:hover:text-primary-400"
              title="View History"
            >
              <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
            {note.can_edit && (
              <button 
                onClick={handleEdit}
                className="p-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                title="Edit Note"
              >
                <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4h6a2 2 0 012 2v6m-3 5l-7 7H4a2 2 0 01-2-2v-4l7-7" />
                </svg>
              </button>
            )}
            {isAdmin && (
              <button 
                onClick={() => setDeleteDialogOpen(true)}
                className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                title="Delete Note"
              >
                <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        </div>
        
        <hr className="my-4 border-ui-light-border dark:border-ui-dark-border" />

        <div className="prose prose-sm sm:prose lg:prose-lg dark:prose-invert max-w-none">
          <ReactMarkdown
            components={createMarkdownComponents(isDarkMode)}
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
          >
            {note.content}
          </ReactMarkdown>
        </div>
      </div>

      <SharedNoteDialog 
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onNoteUpdated={handleNoteUpdated}
        note={note}
      />

      <SharedNoteVersions
        open={versionsOpen}
        onClose={() => setVersionsOpen(false)}
        noteId={noteId}
        noteTitle={note.title}
      />
      
      {deleteDialogOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div 
            className="absolute inset-0 bg-black opacity-50"
            onClick={() => setDeleteDialogOpen(false)}
          ></div>
          <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-md shadow-lg z-10 max-w-sm w-full mx-4 p-6">
            <h3 className="text-lg font-semibold mb-4 text-ui-light-text-primary dark:text-ui-dark-text-primary">Confirm Deletion</h3>
            <p className="mb-6 text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
              Are you sure you want to delete this shared note? This action cannot be undone and will also delete all version history.
            </p>
            <div className="flex justify-end space-x-2">
              <button 
                onClick={() => setDeleteDialogOpen(false)}
                className="px-4 py-2 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary hover:text-ui-light-text-primary dark:hover:text-ui-dark-text-primary"
              >
                Cancel
              </button>
              <button 
                onClick={handleDelete}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SharedNoteDetail;